<template>
  <div class="accordion" id="pricingAccordion">
    <div
      v-for="(window, index) in pricingWindows"
      :key="index"
      class="accordion-item"
    >
      <h2 class="accordion-header" :id="'heading' + index">
        <button
          class="accordion-button d-flex align-items-center justify-content-between"
          type="button"
          data-bs-toggle="collapse"
          :data-bs-target="'#collapse' + index"
          aria-expanded="true"
          :aria-controls="'collapse' + index"
        >
          <span class="title-group w-100">
            <i class="fa fa-calendar" aria-hidden="true"></i>
            <strong>{{ window.name }}</strong>
            <span class="day-header-display"
              >{{ window.daysIn }} - {{ window.daysOut }} Days Out</span
            >
          </span>
          <div class="accordion-action-icons actions-group d-flex ms-auto">
            <i
              role="button"
              class="fa fa-copy fa-lg action-icon"
              :class="{
                'action-icon--disabled': !currentStrategy.isMultipleWindows,
              }"
              @click.stop="
                currentStrategy.isMultipleWindows && copyPricingWindow(window)
              "
              :data-bs-toggle="currentStrategy.isMultipleWindows && 'modal'"
              data-bs-target="#copyPriceWindowModal"
              :disabled="!currentStrategy.isMultipleWindows"
              v-tooltip="{
                content: '<center>Copy Price Windows <br/> Settings </center>',
                placement: 'bottom',
                triggers: ['hover'],
                html: true,
              }"
            ></i>
            <i
              role="button"
              class="fa fa-save fa-lg action-icon ms-2"
              @click.stop="savePricingStrategy(window)"
            ></i>
            <i
              role="button"
              class="fa fa-trash-o fa-lg action-icon ms-2"
              :class="{
                'action-icon--disabled': !currentStrategy.isMultipleWindows,
              }"
              @click.stop="
                currentStrategy.isMultipleWindows && deletePricingWindow(window)
              "
            ></i>
          </div>
        </button>
      </h2>

      <div
        :id="'collapse' + index"
        class="accordion-collapse collapse show"
        :aria-labelledby="'heading' + index"
        data-bs-parent="#pricingAccordion"
      >
        <div class="accordion-body">
          <PricingAllDaysStrategyComponent
            v-if="strategyType === 1"
            :windowProp="window"
            :getUtilizationPrice="getUtilizationPrice"
            :getUsePercent="getUsePercent"
            :updateUtilizationPrice="updateUtilizationPrice"
            :updateUsePercent="updateUsePercent"
            :currentStrategy="currentStrategy"
            :utilizationPricesByWindowId="utilizationPricesByWindowId"
            :handlePricingSaveSuccess="handlePricingSaveSuccess"
            :handlePricingSaveError="handlePricingSaveError"
            v-model:dailyUtilizationSettingsRef="
              dailyUtilizationSettingsRefs[index]
            "
            :invalidDaysIn="invalidDaysIn"
            :invalidDaysOut="invalidDaysOut"
            :daysInMoreThanDayOut="daysInMoreThanDayOut"
            :invalidName="invalidName"
            @updateWindow="handleUpdateWindow"
          />

          <PricingWeekStrategyComponent
            v-if="strategyType === 2"
            :windowProp="window"
            :getUtilizationPrice="getUtilizationPrice"
            :getUsePercent="getUsePercent"
            :updateUtilizationPrice="updateUtilizationPrice"
            :updateUsePercent="updateUsePercent"
            :daysOfWeek="daysOfWeek"
            :utilizationPricesByWindowId="utilizationPricesByWindowId"
            :handlePricingSaveSuccess="handlePricingSaveSuccess"
            :handlePricingSaveError="handlePricingSaveError"
            :currentStrategy="currentStrategy"
            v-model:dailyUtilizationSettingsRef="
              dailyUtilizationSettingsRefs[index]
            "
            :invalidDaysIn="invalidDaysIn"
            :invalidDaysOut="invalidDaysOut"
            :daysInMoreThanDayOut="daysInMoreThanDayOut"
            :invalidName="invalidName"
            @updateWindow="handleUpdateWindow"
            @season-updated="$emit('season-updated')"
          />

          <PricingHourStrategyComponent
            v-if="strategyType === 3"
            :windowIndex="index"
            :initialData="utilizationPricesByWindowId[window.id as number]"
            :priceWindowId="window.id"
            :invalidDaysIn="invalidDaysIn"
            :invalidDaysOut="invalidDaysOut"
            :daysInMoreThanDayOut="daysInMoreThanDayOut"
            :invalidName="invalidName"
            @addUtilizationPrice="handleAddUtilizationPrice"
            @updateInitialData="
              handleUpdateUtilizationPrices(window.id ?? 0, $event)
            "
            @save-success="handlePricingSaveSuccess"
            @save-error="handlePricingSaveError"
            :ref="getHourlyUtilizationRef(index)"
            :currentStrategy="currentStrategy"
            :windowProp="window"
            @updateWindow="handleUpdateWindow"
          />
        </div>
      </div>
    </div>

    <CopyPriceWindowModal
      :pricingWindows="pricingWindows"
      :pricingWindow="selectedPricingWindow"
      :strategyType="strategyType"
      @price-window-copied="onPriceWindowCopied"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  toRef,
  reactive,
  ComponentPublicInstance,
  watch,
  provide,
  onMounted,
} from 'vue';

import PricingAllDaysStrategyComponent from './PricingAllDaysStrategyComponent.vue';
import PricingHourStrategyComponent from './PricingHourStrategyComponent.vue';
import PricingWeekStrategyComponent from './PricingWeekStrategyComponent.vue';
import CopyPriceWindowModal from './CopyPriceWindowModal.vue';

import {
  DailyUtilization,
  PriceWindow,
  PriceWindowUtilization,
  Strategy,
  TimeWindow,
  UtilizationPrice,
} from '@/api';
import { pricingApi, utilizationPriceApi } from '@/apiClient';
import { UtilizationMapper } from '@/utils/utilization-mapper';
import { DayOfWeek, Utilization } from '@/models';

import { useToast } from 'vue-toastification';
import eventBus, { EventType } from '@/eventBus';
import { AllDay } from '@/utils/dateUtils';

export default defineComponent({
  components: {
    PricingHourStrategyComponent,
    PricingAllDaysStrategyComponent,
    PricingWeekStrategyComponent,
    CopyPriceWindowModal,
  },
  name: 'PricingAccordionComponent',
  props: {
    strategy: {
      type: Object as PropType<Strategy>,
      required: true,
    },
    strategyType: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const defaultDaysInValue = 0;
    const defaultDaysOutValue = 9999;

    const finiteDayBound = (value: unknown): number | undefined => {
      if (value === null || value === undefined) return undefined;
      if (typeof value === 'string' && value.trim() === '') return undefined;
      const n = Number(value);
      return Number.isFinite(n) ? n : undefined;
    };

    const normalizeDayBound = (value: unknown, fallback: number): number => {
      const n = finiteDayBound(value);
      return n !== undefined ? n : fallback;
    };

    const currentStrategy = toRef(props, 'strategy');
    const pricingWindows = ref([] as PriceWindow[]);
    const fetchedPricingWindows = ref([] as PriceWindow[]);
    const windowUtilizations = ref([] as Utilization[]);
    const utilizationPricesByWindowId = reactive<
      Record<number, UtilizationPrice[]>
    >({});
    const dailyUtilizationSettingsRefs = ref<
      (ComponentPublicInstance | null)[]
    >([]);
    const hourlyUtilizationComponentRefs = ref<
      (ComponentPublicInstance | null)[]
    >([]);
    const selectedPricingWindow = ref<PriceWindow | null>(null);
    const newWindowId = ref<number | null>(null);
    const toast = useToast();

    watch(
      () => props.strategyType,
      async (newVal: number) => {
        try {
          saveStrategy();
          let resetFailure = false;
          for (const window of pricingWindows.value) {
            const id = window.id;
            if (id == null || !Number.isFinite(id) || id <= 0) continue;
            try {
              await pricingApi.pricingDetailsResetPost(id);
            } catch (e) {
              resetFailure = true;
              console.warn(
                'Reset utilizations failed for window',
                id,
                (e as { response?: { status?: number } })?.response?.status,
                e
              );
            }
          }
          if (resetFailure) {
            toast.warning(
              'One or more pricing windows could not be reset. The list will refresh.'
            );
          }
          await fetchPricingWindow();
        } catch (error) {
          console.error('Error in strategy-type change:', error);
          toast.error(
            'Could not complete the strategy change. Please try again.'
          );
        }

        emit('strategyTypeChanged', newVal);
      }
    );

    const getHourlyUtilizationRef =
      (index: number) => (el: ComponentPublicInstance) => {
        hourlyUtilizationComponentRefs.value[index] = el;
      };

    async function fetchPricingWindow() {
      try {
        if (!currentStrategy.value) return;

        const response = await pricingApi.pricingGet(currentStrategy.value.id);
        fetchedPricingWindows.value = response.data ?? [];

        const preserveWindows =
          fetchedPricingWindows.value.length === 0 &&
          pricingWindows.value.length > 0;
        const windowsToProcess = preserveWindows
          ? pricingWindows.value
          : fetchedPricingWindows.value;

        if (!preserveWindows) {
          pricingWindows.value = fetchedPricingWindows.value;
        }

        windowUtilizations.value.length = 0; //reset utilizations if any

        for (const window of windowsToProcess) {
          const id = window.id;
          if (id == null || !Number.isFinite(id) || id <= 0) {
            continue;
          }
          let utilizationPricesResponse = await fetchUtilization(id);
          if (props.strategyType === 3 && utilizationPricesResponse.length) {
            utilizationPricesResponse = utilizationPricesResponse.filter(
              (util) => util.dayOfTheWeek !== 0
            );
          }

          if (utilizationPricesResponse) {
            const utilizations = UtilizationMapper(utilizationPricesResponse);
            windowUtilizations.value.push(...utilizations);

            const checked = checkInitialDataForChildComponent(
              utilizationPricesResponse
            );
            utilizationPricesByWindowId[id] = checked!;
          }
        }
      } catch (error) {
        console.error(
          'Error fetching pricing windows and utilizations:',
          error
        );
      }
    }

    const checkInitialDataForChildComponent = (
      utilizationsResponse: UtilizationPrice[]
    ): UtilizationPrice[] => {
      return props.strategyType === 2
        ? utilizationsResponse.length <= 1
          ? defaultUtilizationsByDayOfWeek()
          : utilizationsResponse
        : utilizationsResponse;
    };

    const defaultUtilizationsByDayOfWeek = (): UtilizationPrice[] => {
      //Default utilizations per week day
      const currentDate = new Date().toISOString().split('T')[0];
      const defaultUtilizations: UtilizationPrice[] = daysOfWeek
        .filter((day) => day.value !== DayOfWeek.AllWeek)
        .map((day) => ({
          dayOfTheWeek: day.value,
          startTime: AllDay.startTime,
          endTime: AllDay.endTime,
          timeWindowDescription: 'All Day',
          low: 0,
          high: 100,
          pricePercentage: 100,
          priceAmount: 0,
          usePercent: true,
          id: 0,
        }));
      return defaultUtilizations;
    };

    async function fetchUtilization(priceWindowId: number) {
      if (!Number.isFinite(priceWindowId) || priceWindowId <= 0) {
        return [];
      }
      try {
        const response = await utilizationPriceApi.utilizationPriceGet(
          priceWindowId
        );
        return response.data as UtilizationPrice[];
      } catch (error) {
        console.error('Error fetching utilization:', error);
        return [];
      }
    }

    const daysOfWeek = [
      //{ name: 'AllWeek', value: DayOfWeek.AllWeek },
      { name: 'Monday', value: DayOfWeek.Monday },
      { name: 'Tuesday', value: DayOfWeek.Tuesday },
      { name: 'Wednesday', value: DayOfWeek.Wednesday },
      { name: 'Thursday', value: DayOfWeek.Thursday },
      { name: 'Friday', value: DayOfWeek.Friday },
      { name: 'Saturday', value: DayOfWeek.Saturday },
      { name: 'Sunday', value: DayOfWeek.Sunday },
    ];

    // HERE
    const handlePricingSaveSuccess = () => {
      console.log('Utilization prices saved successfully');
      emit('season-updated');
    };

    // HERE
    const handlePricingSaveError = (error: any) => {
      console.error('Error saving utilization prices:', error);
      emit('season-updated');
    };

    const findUtilizationPrice = (windowId?: number) => {
      return windowUtilizations.value.find(
        (up) => up.priceWindowId === windowId
      );
    };

    const getUtilization = (
      windowId: number | undefined,
      dayOfWeek: number,
      timeWindowIndex = 0,
      rangeIndex = 0
    ) => {
      if (windowId === undefined) return null;

      // Find the utilization for the specified windowId
      const utilization = windowUtilizations.value.find(
        (util) => util.priceWindowId === windowId
      );

      if (!utilization) {
        return null;
      }

      // Function to find a daily utilization for a specific dayOfWeek
      const findDailyUtilization = (dayOfWeek: number) =>
        utilization.dailyUtilizations.find((du) => du.dayOfWeek === dayOfWeek);
      // Try to get the DailyUtilization for the specified dayOfWeek
      let dailyUtilization = findDailyUtilization(dayOfWeek);

      // If dayOfWeek is 0 (AllWeek) or no time windows exist, search for the first day that has time windows
      if (dayOfWeek === 0 || !dailyUtilization?.timeWindows?.length) {
        //If strategy is -by day of week-, set a default time window
        if (props.strategyType === 2 && dailyUtilization) {
          dailyUtilization.timeWindows = [getDefaultTimeWindow()];
        } else {
          dailyUtilization = utilization.dailyUtilizations.find(
            (du) => du.timeWindows && du.timeWindows.length > 0
          );
        }
      }

      if (
        !dailyUtilization ||
        !dailyUtilization.timeWindows ||
        !dailyUtilization.timeWindows.length
      ) {
        return null;
      }

      // Get the time window and utilization range
      const timeWindow = dailyUtilization.timeWindows[timeWindowIndex];
      if (
        !timeWindow ||
        !timeWindow.utilizationRanges ||
        !timeWindow.utilizationRanges.length
      ) {
        return null;
      }

      const utilizationRange = timeWindow.utilizationRanges[rangeIndex];
      if (!utilizationRange) {
        return null;
      }

      return utilizationRange;
    };

    // Helper function to generate the default daily utilization
    const getDefaultDailyUtilization = (dayOfWeek: number) => {
      return {
        dayOfWeek: dayOfWeek as DayOfWeek,
        timeWindows: [getDefaultTimeWindow()],
      };
    };

    // Helper function to generate the default time windows
    const getDefaultTimeWindow = () => {
      return {
        timeWindowDescription: '',
        startTime: new Date(AllDay.startTime),
        endTime: new Date(AllDay.endTime),
        utilizationRanges: [
          {
            low: 0,
            high: 100,
            pricePercentage: 100,
            priceAmount: 0,
            id: 0,
            usePercent: true,
          },
        ],
      };
    };

    const getDefaultUtilizationPrice = () => {
      return {
        dayOfTheWeek: 0,
        startTime: new Date(AllDay.startTime).toISOString(),
        endTime: new Date(AllDay.endTime).toISOString(),
        low: 0,
        high: 100,
        pricePercentage: 100,
        priceAmount: 0,
        timeWindowDescription: '',
        usePercent: true,
      } as UtilizationPrice;
    };

    const getUtilizationPrice = (
      windowId: number | undefined,
      dayOfWeek: number,
      timeWindowIndex = 0,
      rangeIndex = 0
    ) => {
      const utilizationRange = getUtilization(
        windowId,
        dayOfWeek,
        timeWindowIndex,
        rangeIndex
      );

      if (!utilizationRange) return 0;

      return utilizationRange.usePercent
        ? utilizationRange.pricePercentage ?? 0
        : utilizationRange.priceAmount ?? 0;
    };

    const getUsePercent = (
      windowId: number | undefined,
      dayOfWeek: number,
      timeWindowIndex = 0,
      rangeIndex = 0
    ) => {
      const utilizationRange = getUtilization(
        windowId,
        dayOfWeek,
        timeWindowIndex,
        rangeIndex
      );

      return utilizationRange?.usePercent ?? false;
    };

    const updateUtilizationPrice = (
      windowId: number,
      dayOfWeek: number,
      newValue: number
    ) => {
      const utilization = windowUtilizations.value.find(
        (up) => up.priceWindowId === windowId
      );
      if (!utilization) return;

      const dailyUtilization = utilization.dailyUtilizations.find(
        (du) => du.dayOfWeek === dayOfWeek
      );
      if (!dailyUtilization) return;

      const timeWindow = dailyUtilization.timeWindows?.[0];
      if (!timeWindow) return;

      const utilizationRange = timeWindow.utilizationRanges?.[0];
      if (!utilizationRange) return;

      if (utilizationRange.usePercent) {
        utilizationRange.pricePercentage = newValue;
      } else {
        utilizationRange.priceAmount = newValue;
      }
    };

    const updateUsePercent = (
      windowId: number,
      dayOfWeek: number,
      newUsePercent: boolean
    ) => {
      const utilization = windowUtilizations.value.find(
        (up) => up.priceWindowId === windowId
      );
      if (!utilization) return;

      const dailyUtilization = utilization.dailyUtilizations.find(
        (du) => du.dayOfWeek === dayOfWeek
      );
      if (!dailyUtilization) return;

      const timeWindow = dailyUtilization.timeWindows?.[0];
      if (!timeWindow) return;

      const utilizationRange = timeWindow.utilizationRanges?.[0];
      if (!utilizationRange) return;

      utilizationRange.usePercent = newUsePercent;

      // When switching between percentage and amount, we need to ensure the correct property is set
      if (newUsePercent) {
        utilizationRange.pricePercentage = utilizationRange.priceAmount ?? 0;
        utilizationRange.priceAmount = null;
      } else {
        utilizationRange.priceAmount = utilizationRange.pricePercentage ?? 0;
        utilizationRange.pricePercentage = 100;
      }
    };

    const copyPricingWindow = (pricingWindow: PriceWindow) => {
      selectedPricingWindow.value = pricingWindow;
    };

    const calculateNewPriceAmount = (price: UtilizationPrice): number => {
      if (price.usePercent && price.pricePercentage) {
        return (price.high || 0) * (price.pricePercentage / 100);
      }
      return price.priceAmount || 0;
    };

    const pickDayWithMostRanges = (
      prices: UtilizationPrice[]
    ): UtilizationPrice[] => {
      if (!prices?.length || prices.length <= 1) return prices ?? [];
      const byDay = new Map<number, UtilizationPrice[]>();
      for (const p of prices) {
        const d = p.dayOfTheWeek ?? 0;
        if (!byDay.has(d)) byDay.set(d, []);
        byDay.get(d)!.push(p);
      }
      let best: UtilizationPrice[] = [];
      for (const arr of byDay.values()) {
        if (arr.length > best.length) best = arr;
      }
      return best;
    };

    const updateUtilizationPrices = (
      utilizations: Utilization[],
      priceWindowId: number,
      priceAdjustmentValue: number
    ) => {
      utilizations.forEach((utilization) => {
        if (utilization.priceWindowId === priceWindowId) {
          utilization.dailyUtilizations.forEach((dailyUtilization) => {
            dailyUtilization.timeWindows?.forEach((timeWindow) => {
              timeWindow.utilizationRanges?.forEach((range) => {
                // Update pricePercentage and priceAmount
                range.pricePercentage =
                  (range.pricePercentage || 0) + priceAdjustmentValue;
                range.priceAmount = calculateNewPriceAmount(range);
              });
            });
          });
        }
      });
    };

    const createUtilizationPrice = (
      range: {
        id?: number;
        low?: number;
        high?: number;
        pricePercentage?: number | null;
        priceAmount?: number | null;
        usePercent?: boolean;
        timeWindowDescription?: string | null;
      },
      timeWindow: TimeWindow,
      dayOfWeek: number | undefined,
      priceWindowId: number
    ): UtilizationPrice => ({
      id: range.id,
      priceWindowId,
      dayOfTheWeek: dayOfWeek ?? 0,
      startTime: timeWindow.startTime ?? AllDay.startTime,
      endTime: timeWindow.endTime ?? AllDay.endTime,
      low: range.low,
      high: range.high,
      pricePercentage: range.pricePercentage,
      priceAmount: range.priceAmount,
      usePercent: range.usePercent,
      timeWindowDescription:
        timeWindow.timeWindowDescription ?? range.timeWindowDescription,
    });

    const getUtilizationPricesFromComponent = (
      currentPricingWindowId: number
    ): UtilizationPrice[] => {
      const utilizationComponent = dailyUtilizationSettingsRefs.value.find(
        (component) =>
          component &&
          (component as any).priceWindowId === currentPricingWindowId
      ) as
        | (ComponentPublicInstance & {
            utilizationData?: DailyUtilization[];
            utilizationDay?: DailyUtilization;
          })
        | undefined;

      const unwrap = (r: unknown) =>
        r != null && typeof r === 'object' && 'value' in r
          ? (r as { value: unknown }).value
          : r;
      const rawData = (utilizationComponent as any)?.utilizationData;
      const dataArr = (
        Array.isArray(unwrap(rawData)) ? unwrap(rawData) : []
      ) as DailyUtilization[];
      const dayObj = unwrap((utilizationComponent as any)?.utilizationDay) as
        | DailyUtilization
        | undefined;

      const newUtilizations: UtilizationPrice[] = [];

      if (props.strategyType === 1 && dayObj) {
        dayObj.timeWindows?.forEach((timeWindow) => {
          if (timeWindow.utilizationRanges) {
            timeWindow.utilizationRanges.forEach((range) => {
              newUtilizations.push(
                createUtilizationPrice(
                  range,
                  timeWindow,
                  0,
                  currentPricingWindowId
                )
              );
            });
          }
        });
        return newUtilizations;
      }

      if (!utilizationComponent) return [];

      if (props.strategyType === 1) {
        const day0Data = dataArr.find(
          (daily: DailyUtilization) => daily.dayOfWeek === 0
        );

        if (day0Data) {
          day0Data.timeWindows?.forEach((timeWindow) => {
            if (timeWindow.utilizationRanges) {
              timeWindow.utilizationRanges.forEach((range) => {
                newUtilizations.push(
                  createUtilizationPrice(
                    range,
                    timeWindow,
                    0,
                    currentPricingWindowId
                  )
                );
              });
            }
          });
        } else {
          let dayWithMostRanges: DailyUtilization | undefined;
          let maxRangesCount = 0;
          dataArr.forEach((daily: DailyUtilization) => {
            let rangesCount = 0;
            daily.timeWindows?.forEach((tw) => {
              rangesCount += tw.utilizationRanges?.length || 0;
            });
            if (daily.dayOfWeek === 0 && rangesCount > 0) {
              if (rangesCount >= maxRangesCount) {
                maxRangesCount = rangesCount;
                dayWithMostRanges = daily;
              }
            } else if (daily.dayOfWeek !== 0 && rangesCount > maxRangesCount) {
              maxRangesCount = rangesCount;
              dayWithMostRanges = daily;
            }
          });
          if (dayWithMostRanges) {
            dayWithMostRanges.timeWindows?.forEach((timeWindow) => {
              if (timeWindow.utilizationRanges) {
                timeWindow.utilizationRanges.forEach((range) => {
                  newUtilizations.push(
                    createUtilizationPrice(
                      range,
                      timeWindow,
                      0,
                      currentPricingWindowId
                    )
                  );
                });
              }
            });
          }
        }
        return newUtilizations;
      }

      dataArr.forEach((daily: DailyUtilization) => {
        if (daily.dayOfWeek === 0) return;
        daily.timeWindows?.forEach((timeWindow) => {
          if (timeWindow.utilizationRanges) {
            timeWindow.utilizationRanges.forEach((range) => {
              newUtilizations.push(
                createUtilizationPrice(
                  range,
                  timeWindow,
                  daily.dayOfWeek,
                  currentPricingWindowId
                )
              );
            });
          }
        });
      });
      if (
        newUtilizations.length <= 1 &&
        dayObj?.timeWindows?.[0]?.utilizationRanges &&
        dayObj.timeWindows[0].utilizationRanges.length > 1
      ) {
        newUtilizations.length = 0;
        dayObj.timeWindows[0].utilizationRanges.forEach(
          (range: {
            id?: number;
            low?: number;
            high?: number;
            pricePercentage?: number | null;
            priceAmount?: number | null;
            usePercent?: boolean;
            timeWindowDescription?: string | null;
          }) => {
            newUtilizations.push(
              createUtilizationPrice(
                range,
                dayObj.timeWindows![0],
                dayObj.dayOfWeek,
                currentPricingWindowId
              )
            );
          }
        );
      }
      return newUtilizations;
    };

    const onPriceWindowCopied = (
      copyToPricingWindow: PriceWindow,
      priceAdjustmentValue: number,
      keepExistingWindows: boolean,
      isNewPricingWindow: boolean
    ) => {
      if (isNewPricingWindow) {
        copyToPricingWindow.name = newDefaultName();
        const updatedPricingWindows = [
          ...pricingWindows.value,
          copyToPricingWindow,
        ];
        pricingWindows.value = [...updatedPricingWindows];

        const currentPricingWindowId = selectedPricingWindow.value?.id;
        if (
          currentPricingWindowId !== undefined &&
          copyToPricingWindow.id !== undefined
        ) {
          let currentWindowPrices =
            utilizationPricesByWindowId[currentPricingWindowId];
          if (props.strategyType === 1 || props.strategyType === 2) {
            const fromComponent = getUtilizationPricesFromComponent(
              currentPricingWindowId
            );
            if (fromComponent.length > 0) currentWindowPrices = fromComponent;
          }

          const copyWindowPrices: UtilizationPrice[] = [];
          const pricesToCopy =
            props.strategyType === 1
              ? pickDayWithMostRanges(currentWindowPrices ?? [])
              : currentWindowPrices ?? [];
          if (pricesToCopy.length > 0) {
            pricesToCopy.forEach((price) => {
              const newPrice = {
                ...price,
                priceWindowId: copyToPricingWindow.id!,
                dayOfTheWeek: props.strategyType === 1 ? 0 : price.dayOfTheWeek,
                id: -(price.id ?? 0) || -1,
                pricePercentage:
                  (price.pricePercentage || 0) + priceAdjustmentValue,
                priceAmount: calculateNewPriceAmount(price),
                usePercent: price.usePercent,
              };
              copyWindowPrices.push(newPrice);
            });
          }
          utilizationPricesByWindowId[copyToPricingWindow.id!] = [
            ...copyWindowPrices,
          ];
          const newUtilizations = UtilizationMapper(copyWindowPrices);
          windowUtilizations.value.push(...newUtilizations);
        }
      } else {
        const currentPricingWindowId = selectedPricingWindow.value?.id;

        if (currentPricingWindowId !== undefined) {
          let currentWindowPrices =
            utilizationPricesByWindowId[currentPricingWindowId];
          if (props.strategyType === 1 || props.strategyType === 2) {
            const fromComponent = getUtilizationPricesFromComponent(
              currentPricingWindowId
            );
            if (fromComponent.length > 0) currentWindowPrices = fromComponent;
          }

          const pricesToCopyExisting =
            props.strategyType === 1
              ? pickDayWithMostRanges(currentWindowPrices ?? [])
              : currentWindowPrices ?? [];

          if (pricesToCopyExisting.length > 0) {
            if (copyToPricingWindow.id !== undefined) {
              updateUtilizationPrices(
                windowUtilizations.value,
                copyToPricingWindow.id,
                priceAdjustmentValue
              );
              const copyWindowPrices =
                utilizationPricesByWindowId[copyToPricingWindow.id] ??
                (props.strategyType === 3 ? [] : null);

              if (copyWindowPrices) {
                copyWindowPrices.length = 0;
                pricesToCopyExisting.forEach((price) => {
                  const newPrice = {
                    ...price,
                    priceWindowId: copyToPricingWindow.id,
                    dayOfTheWeek:
                      props.strategyType === 1 ? 0 : price.dayOfTheWeek,
                    id: -(price.id ?? 0) || -1,
                    pricePercentage:
                      (price.pricePercentage || 0) + priceAdjustmentValue,
                    priceAmount: calculateNewPriceAmount(price),
                    usePercent: price.usePercent,
                  };
                  copyWindowPrices.push(newPrice);
                });
                utilizationPricesByWindowId[copyToPricingWindow.id] = [
                  ...copyWindowPrices,
                ];

                windowUtilizations.value = windowUtilizations.value.filter(
                  (util) => util.priceWindowId !== copyToPricingWindow.id
                );

                const newUtilizations = UtilizationMapper(copyWindowPrices);
                windowUtilizations.value.push(...newUtilizations);
              }
            }
          }
        }
      }
      selectedPricingWindow.value = null;
    };

    const savePricingStrategy = async (window: PriceWindow) => {
      const errors = document.querySelector('.form-control-error');
      if (errors) {
        toast.error(
          'Cannot save strategy due to validation errors. Please check each price window to correct the errors.'
        );
      } else {
        //save pricing window before saving pricing utilization
        if (await savePricingWindow(window)) {
          //save pricing window utilization
          if (currentStrategy.value.isAdjustByUtilization) {
            //process combo strategy
            await savePriceAdjustmentsFromComponent(window);
          } else if (props.strategyType === 1 || props.strategyType === 2) {
            //process days-out strategy (all days - by day of week)
            const utilizations = [];
            utilizations.push(
              windowUtilizations.value
                .find((up) => up.priceWindowId === window.id)
                ?.dailyUtilizations.filter(
                  (u) =>
                    (props.strategyType === 1 && u.dayOfWeek === 0) || //-all days- strategy uses day 0
                    (props.strategyType === 2 && u.dayOfWeek !== 0) //-by day of week- strategy must have a time window per day
                )
            );

            if (utilizations?.length) {
              const dailyUtilizationsData = new Array<DailyUtilization>();
              [...utilizations][0]?.forEach((u) => {
                dailyUtilizationsData.push(u as DailyUtilization);
              });

              //make days-out strategies available all day
              dailyUtilizationsData.map((util) => {
                util.timeWindows?.map((window) => {
                  window.startTime = AllDay.startTime;
                  window.endTime = AllDay.endTime;
                });
              });

              const priceWindowUtilization: PriceWindowUtilization = {
                priceWindowId: newWindowId.value ?? window.id,
                dailyUtilizations: dailyUtilizationsData,
              };
              postPricingWindowUtilization(priceWindowUtilization);
            } else if (props.strategyType === 1) {
              console.log(
                `Cannot save price window. Utilization not found for window ${window.id}.`
              );
            }
          } else {
            //process days-out strategy (hourly)
            await savePriceAdjustmentsFromComponent(window);
          }
        }
      }
      newWindowId.value = null; //reset to enable for a new window
    };

    const deletePricingWindow = async (window: PriceWindow) => {
      if (confirm('Are you sure you want to delete this pricing window?')) {
        try {
          const response = await pricingApi.pricingDelete(
            window.id,
            parseInt(currentStrategy.value.id?.toString() ?? '0')
          );

          if (response.status >= 200 && response.status < 300) {
            // Remove from all relevant state
            pricingWindows.value = pricingWindows.value.filter(
              (w) => w.id !== window.id
            );
            fetchedPricingWindows.value = fetchedPricingWindows.value.filter(
              (w) => w.id !== window.id
            );
            if (window.id !== undefined) {
              delete utilizationPricesByWindowId[window.id];
            }
            toast.success('Pricing window deleted successfully');
          } else {
            toast.error(
              'Failed to delete the pricing window. Please try again.'
            );
          }
        } catch (error: any) {
          const status = error?.response?.status;
          const isAlreadyGone =
            error?.response?.data?.message ===
              'Sequence contains no elements' ||
            status === 400 ||
            status === 404;
          if (isAlreadyGone) {
            pricingWindows.value = pricingWindows.value.filter(
              (w) => w.id !== window.id
            );
            fetchedPricingWindows.value = fetchedPricingWindows.value.filter(
              (w) => w.id !== window.id
            );
            if (window.id !== undefined) {
              delete utilizationPricesByWindowId[window.id];
            }
            toast.success('Pricing window deleted successfully');
          } else {
            console.error('Error deleting pricing window:', error);
            toast.error(
              'Failed to delete the pricing window. Please try again.'
            );
          }
        }
      }
    };

    const savePriceAdjustmentsFromComponent = async (window: PriceWindow) => {
      const utilizationRefs =
        props.strategyType === 1 || props.strategyType === 2
          ? dailyUtilizationSettingsRefs //For All-Days and Week use the save method in UtilizationPricingSettings.vue
          : hourlyUtilizationComponentRefs; //For Hourly use the save method in PricingHourStrategyComponent.vue

      // Find the correct component instance based on the window id
      const utilizationComponent = utilizationRefs.value.find(
        (component) =>
          component && (component as any).priceWindowId === window.id
      ) as
        | (ComponentPublicInstance & {
            triggerSave?: (newWindowId: number | undefined) => Promise<void>;
          })
        | undefined;

      if (utilizationComponent) {
        if (typeof utilizationComponent.triggerSave === 'function') {
          try {
            // Call the triggerSave method on the child component
            await utilizationComponent.triggerSave(
              newWindowId.value ?? window.id
            );

            // Refresh utilizations prices from utilization component
            // Hourly component handles the refresh
            if (props.strategyType !== 3) {
              const unwrap = (x: unknown) =>
                x != null &&
                typeof x === 'object' &&
                'value' in x &&
                (x as { value: unknown }).value !== undefined
                  ? (x as { value: unknown }).value
                  : x;
              const rawData = unwrap(
                (utilizationComponent as { utilizationData?: unknown })
                  .utilizationData
              );
              const rawDay = unwrap(
                (utilizationComponent as { utilizationDay?: unknown })
                  .utilizationDay
              );
              const isAllWeek =
                (utilizationComponent as { isAllWeek?: boolean }).isAllWeek ===
                true;

              const newUtilizations: UtilizationPrice[] = [];

              if (
                isAllWeek &&
                rawDay &&
                typeof rawDay === 'object' &&
                'timeWindows' in rawDay
              ) {
                const tw = (rawDay as { timeWindows?: unknown[] })
                  .timeWindows?.[0];
                const ranges = (tw as { utilizationRanges?: unknown[] })
                  ?.utilizationRanges;
                if (Array.isArray(ranges)) {
                  ranges.forEach((range: unknown) => {
                    const r = range as Record<string, unknown>;
                    newUtilizations.push({
                      ...r,
                      priceWindowId: newWindowId.value ?? window.id,
                      dayOfTheWeek: 0,
                      startTime:
                        (tw as { startTime?: string })?.startTime ||
                        AllDay.startTime,
                      endTime:
                        (tw as { endTime?: string })?.endTime || AllDay.endTime,
                      timeWindowDescription:
                        (tw as { timeWindowDescription?: string })
                          ?.timeWindowDescription ?? 'All Day',
                    } as UtilizationPrice);
                  });
                }
              } else if (Array.isArray(rawData)) {
                rawData.forEach((daily: { timeWindows?: unknown[] }) =>
                  daily.timeWindows?.forEach((tw: unknown) => {
                    const timeWindow = tw as Record<string, unknown>;
                    const ranges = timeWindow.utilizationRanges as
                      | unknown[]
                      | undefined;
                    ranges?.forEach((range: unknown) => {
                      newUtilizations.push(range as UtilizationPrice);
                    });
                  })
                );
              }

              const key = newWindowId.value ?? window.id;
              if (key != null && Number.isFinite(key)) {
                utilizationPricesByWindowId[key] = newUtilizations;
              }
              if (window.id != null && window.id !== key) {
                utilizationPricesByWindowId[window.id] = newUtilizations;
              }
            }
          } catch (error) {
            console.error('Error saving utilization prices:', error);
          }
        } else {
          console.error(
            'triggerSave method not found on Utilization component'
          );
        }
      } else {
        console.error('Utilization component not found for window:', window.id);
      }
    };

    const saveStrategy = async () => {
      emit('save-strategy');
    };

    const setDefaultDaysRangeValuesIfNone = (window: PriceWindow) => {
      window.daysIn = normalizeDayBound(window.daysIn, defaultDaysInValue);
      window.daysOut = normalizeDayBound(window.daysOut, defaultDaysOutValue);
    };

    const savePricingWindow = async (window: PriceWindow) => {
      saveStrategy();

      try {
        let response;
        setDefaultDaysRangeValuesIfNone(window);

        const isNewWindow =
          !window.id ||
          !fetchedPricingWindows.value.find((w) => w.id === window.id);

        if (isNewWindow) {
          const windowToCreate = {
            ...window,
            id: undefined,
          };
          response = await pricingApi.pricingPost(windowToCreate);
        } else {
          response = await pricingApi.pricingPut(window);
        }

        if (response.data && response.data.id) {
          const newId = response.data.id;
          newWindowId.value = newId;

          if (isNewWindow) {
            fetchedPricingWindows.value.push(response.data);
          } else {
            const index = fetchedPricingWindows.value.findIndex(
              (w) => w.id === newId
            );
            if (index >= 0) {
              fetchedPricingWindows.value[index] = response.data;
            }
          }

          console.log('Pricing window saved successfully:', response.data);
          return true;
        }

        if (
          response.status >= 200 &&
          response.status < 300 &&
          !isNewWindow &&
          window.id
        ) {
          const index = fetchedPricingWindows.value.findIndex(
            (w) => w.id === window.id
          );
          if (index >= 0) {
            fetchedPricingWindows.value[index] = { ...window };
          }

          console.log('Pricing window saved successfully:', window);
          return true;
        }

        return false;
      } catch (error) {
        console.error('Error saving pricing window:', error);
        toast.error('Failed to save pricing window. Please try again.');
        return false;
      }
    };

    const putPricingWindow = async (window: PriceWindow) => {
      try {
        const response = await pricingApi.pricingPut(window);
        console.log('Pricing window updated successfully:', response.data);
        return true;
      } catch (error) {
        console.error('Error updating pricing window:', error);
        return false;
      }
    };

    const postPricingWindow = async (window: PriceWindow) => {
      try {
        const response = await pricingApi.pricingPost(window);
        newWindowId.value =
          response.data.id === undefined ? null : response.data.id;
        console.log('Pricing window created successfully:', response.data);
        return true;
      } catch (error) {
        console.error('Error creating pricing window:', error);
        return false;
      }
    };

    const postPricingWindowUtilization = async (
      utilization: PriceWindowUtilization
    ) => {
      try {
        utilization.dailyUtilizations?.forEach((element) => {
          element?.timeWindows?.forEach((window) => {
            window.utilizationRanges?.forEach((range) => {
              range.priceAmount = range.usePercent ? null : range.priceAmount;
              range.pricePercentage = range.usePercent
                ? range.pricePercentage
                : 100;
            });
          });
        });

        const response = await pricingApi.pricingDetailsPost(utilization);

        // Refresh utilizations prices from POST response
        const newUtilizations: UtilizationPrice[] = [];
        response.data.dailyUtilizations?.map((daily: any) => {
          daily.timeWindows.map((timeWindow: any) => {
            timeWindow.utilizationRanges.map((range: any) => {
              const newRange = {
                ...range,
                priceWindowId: utilization.priceWindowId,
                dayOfTheWeek: daily.dayOfWeek,
              };
              newUtilizations.push(newRange);
            });
          });
        });
        utilizationPricesByWindowId[utilization.priceWindowId!] =
          newUtilizations;

        emit('season-updated');
      } catch (error) {
        console.error('Error saving pricing window:', error);
      }
    };

    const isNumber = (event: KeyboardEvent) => {
      const charCode = event.charCode;
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
      }
    };

    const changedUnit = () => undefined;

    const handleUpdateWindow = (
      window: PriceWindow,
      key: keyof PriceWindow,
      value: string | number
    ) => {
      if (key === 'daysIn' || key === 'daysOut') {
        const n = finiteDayBound(value);
        (window as Record<string, unknown>)[key] = n ?? undefined;
        return;
      }
      (window as any)[key] = value;
    };

    const handleUpdateUtilizationPrices = (
      windowId: number,
      updatedPrices: UtilizationPrice[]
    ) => {
      fetchPricingWindow();
    };

    const invalidDaysIn = (windowId: number) => {
      const currentWindow = pricingWindows.value.find((w) => w.id === windowId);
      if (!currentWindow) return false;
      const curIn = finiteDayBound(currentWindow.daysIn);
      if (curIn === undefined) return false;
      return pricingWindows.value
        .filter((pricingWindow) => pricingWindow.id !== windowId)
        .some((pricingWindow) => {
          const pIn = finiteDayBound(pricingWindow.daysIn);
          const pOut = finiteDayBound(pricingWindow.daysOut);
          if (pIn === undefined || pOut === undefined) return false;
          return curIn >= pIn && curIn <= pOut;
        });
    };

    const invalidDaysOut = (windowId: number) => {
      const currentWindow = pricingWindows.value.find((w) => w.id === windowId);
      if (!currentWindow) return false;
      const curOut = finiteDayBound(currentWindow.daysOut);
      if (curOut === undefined) return false;
      return pricingWindows.value
        .filter((pricingWindow) => pricingWindow.id !== windowId)
        .some((pricingWindow) => {
          const pIn = finiteDayBound(pricingWindow.daysIn);
          const pOut = finiteDayBound(pricingWindow.daysOut);
          if (pIn === undefined || pOut === undefined) return false;
          return curOut >= pIn && curOut <= pOut;
        });
    };

    const daysInMoreThanDayOut = (windowId: number) => {
      const currentWindow = pricingWindows.value.find((w) => w.id === windowId);
      if (!currentWindow) return false;
      const daysIn = finiteDayBound(currentWindow.daysIn);
      const daysOut = finiteDayBound(currentWindow.daysOut);
      if (daysIn === undefined || daysOut === undefined) return false;
      return daysIn > daysOut;
    };

    const invalidName = (windowId: number) => {
      const currentWindow = pricingWindows.value.find(
        (window) => window.id === windowId
      );
      if (currentWindow) {
        const otherWindows = pricingWindows.value.filter(
          (window) => window.id !== windowId
        );
        return otherWindows.some(
          (others) =>
            others.name?.trim().toLowerCase() ===
            currentWindow.name?.trim().toLowerCase()
        );
      }
    };

    const newDefaultName = () => {
      const defaultWindows = pricingWindows.value.filter((w) =>
        w.name?.toLowerCase().trim().startsWith('default')
      );
      return `Default ${defaultWindows.length + 1}`;
    };

    const handleAddUtilizationPrice = (_utilizationPrice: UtilizationPrice) =>
      undefined;

    fetchPricingWindow();

    onMounted(() => {
      eventBus.on(EventType.CreateNewPriceWindow, () => {
        const validIds = pricingWindows.value
          .map((pw) => pw.id)
          .filter(
            (n): n is number => typeof n === 'number' && Number.isFinite(n)
          );
        const maxId = validIds.length ? Math.max(...validIds) : -1;
        const nextId = maxId + 1;
        const newPriceWindow = {
          daysIn: defaultDaysInValue,
          daysOut: defaultDaysOutValue,
          description: '',
          id: Number.isFinite(nextId) && nextId > 0 ? nextId : -1,
          name: 'New Price Window',
          priceAdjustment: 0,
          sortOrder: 0,
          strategyId: currentStrategy.value.id as number,
        };
        const updatedPricingWindows = [...pricingWindows.value, newPriceWindow];
        pricingWindows.value = [...updatedPricingWindows];

        //Set a default blank utilization price for the new window
        //Down-by-hour strategies do not need default utilizations
        if (props.strategyType !== 3) {
          const newWindowPrices: UtilizationPrice[] = [
            {
              id: 0,
              priceWindowId: newPriceWindow.id,
              ...getDefaultUtilizationPrice(),
            },
          ];

          // Update the utilizationPrices reactive reference
          const newUtilizations = UtilizationMapper(newWindowPrices);
          windowUtilizations.value.push(...newUtilizations);

          // Store the new window prices
          if (props.strategyType === 2) {
            const checked = checkInitialDataForChildComponent(newWindowPrices);
            utilizationPricesByWindowId[newPriceWindow.id] = checked!;
          } else {
            utilizationPricesByWindowId[newPriceWindow.id] = newWindowPrices;
          }
        }
      });
    });

    provide('daysOfWeek', daysOfWeek);

    return {
      currentStrategy,
      pricingWindows,
      selectedPricingWindow,
      copyPricingWindow,
      onPriceWindowCopied,
      savePricingStrategy,
      deletePricingWindow,
      isNumber,
      changedUnit,
      findUtilizationPrice,
      getUtilizationPrice,
      updateUtilizationPrice,
      updateUsePercent,
      getUsePercent,
      daysOfWeek,
      handlePricingSaveSuccess,
      handlePricingSaveError,
      utilizationPricesByWindowId,
      dailyUtilizationSettingsRefs,
      handleUpdateWindow,
      handleUpdateUtilizationPrices,
      handleAddUtilizationPrice,
      invalidDaysIn,
      invalidDaysOut,
      daysInMoreThanDayOut,
      invalidName,
      getHourlyUtilizationRef,
    };
  },
});
</script>

<style scoped>
.accordion-action-icons {
  display: flex;
}

.accordion-action-icons i {
  margin-left: 10px;
  cursor: pointer;
}

.form-group label {
  font-weight: 600;
}

.title-group i,
strong {
  margin-right: 10px;
}

/* Add these new styles for consistency */
.form-control:focus,
.form-select:focus {
  border-color: #618183;
  box-shadow: 0 0 0 0.25rem rgba(64, 95, 107, 0.25);
}

.accordion-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #e6f2f2 !important;
  color: #405f6b !important;
}

.accordion-button:not(.collapsed) {
  background-color: #618183 !important;
  color: #ffffff !important;
}

.accordion-button:not(.collapsed) .btn-group i {
  color: #ffffff !important;
}

.accordion-button:focus {
  box-shadow: 0 0 0 0.25rem rgba(64, 95, 107, 0.25);
}

.btn-group i {
  margin: 0 5px;
  cursor: pointer;
  color: #405f6b;
  transition: color 0.3s ease;
}

.price-display {
  display: flex;
  align-items: center;
  background-color: #98b2b4;
  color: white;
  margin-bottom: 0px !important;
}

.bi-info-circle {
  margin-left: 5px;
}

.daysRange-input {
  width: 90px;
  margin-left: 5px;
}

.actions-group {
  margin-right: 20px;
}

.action-icon {
  font-size: 1.2em;
  padding: 5px;
  cursor: pointer;
  transition: color 0.3s ease;
}

.action-icon--disabled {
  color: #ccc;
  cursor: not-allowed;
}

.action-icon:hover:not(.action-icon--disabled) {
  color: #00bfff;
}
</style>
